import React, { useMemo } from "react";
import { getFeaturesData, iconComponents } from "./featuresData";
import { useLanguage } from "@/context/LanguageContext";

interface FeatureDisplayProps {
  currentSection: number;
}

const FeatureDisplay = ({ currentSection }: FeatureDisplayProps) => {
  const { language } = useLanguage();

  // Dynamically get features data based on current language
  const featuresData = useMemo(() => getFeaturesData(language), [language]);

  // Add bounds checking to prevent undefined access
  const validSection = Math.max(
    0,
    Math.min(currentSection, featuresData.length - 1),
  );
  const currentFeature = featuresData[validSection];

  // Add fallback if somehow currentFeature is still undefined
  if (!currentFeature || featuresData.length === 0) {
    return null;
  }

  const IconComponent =
    iconComponents[currentFeature.iconName as keyof typeof iconComponents];

  const getIconColor = (iconName: string) => {
    switch (iconName) {
      case "Car":
        return "text-blue-500";
      case "CheckCircle":
        return "text-yellow-500";
      case "Users":
        return "text-green-500";
      case "Shield":
        return "text-purple-500";
      case "Zap":
        return "text-yellow-500";
      case "Award":
        return "text-red-500";
      case "Globe":
        return "text-cyan-500";
      default:
        return "text-blue-500";
    }
  };

  return (
    <div key={validSection} className="max-w-4xl mx-auto text-center px-4">
      <div className="flex justify-center mb-6 md:mb-8">
        <div className="p-3 md:p-4 bg-white rounded-full shadow-lg">
          <IconComponent
            className={`w-10 h-10 md:w-12 md:h-12 ${getIconColor(currentFeature.iconName)}`}
          />
        </div>
      </div>

      <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-4 md:mb-6">
        {currentFeature.title}
      </h3>

      <p className="text-base md:text-lg text-gray-700 leading-relaxed max-w-2xl mx-auto">
        {currentFeature.description}
      </p>
    </div>
  );
};

export default FeatureDisplay;
